import { ReactNode, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { ChatBubble } from '@/components/chat/ChatBubble';

interface LayoutProps {
  children: ReactNode;
}

const navItems = [
  { path: '/', label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
  { path: '/sessions', label: 'Sessions', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
  { path: '/vehicles', label: 'Vehicles', icon: 'M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0' },
  { path: '/reports', label: 'Reports', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  { path: '/settings', label: 'Settings', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z' },
];

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const { user, logout } = useAuth0();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <div className="min-h-screen bg-obdai-deep">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-30 h-full w-72 bg-obdai-primary border-r border-white/[0.06] bg-[radial-gradient(ellipse_90%_45%_at_50%_0%,rgba(109,124,255,0.22),rgba(232,102,221,0.06)_55%,transparent_75%)] transform transition-transform duration-300 ease-out lg:translate-x-0 shadow-2xl ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo - Big, commanding, with breathing room */}
        <div className="px-8 py-10 flex justify-center">
          <img
            src="/logo-icon-white.png"
            alt="OBDAI"
            className="h-20 w-auto drop-shadow-lg"
          />
        </div>
        <div className="mx-8 mb-6 h-px bg-gradient-to-r from-transparent via-[#6d7cff]/60 to-transparent" />

        {/* Navigation - Premium spacing and styling */}
        <nav className="px-5 space-y-3">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`group relative flex items-center gap-4 px-5 py-4 rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-gradient-to-r from-[#6d7cff] to-[#e866dd] text-white shadow-lg shadow-purple-500/20'
                    : 'text-gray-200 hover:text-white hover:bg-white/[0.06]'
                }`}
              >
                {/* Active indicator bar */}
                {isActive && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full shadow-lg shadow-white/50" />
                )}
                <svg
                  className={`w-6 h-6 transition-all duration-200 ${isActive ? 'text-white' : 'text-[#8b97ff] group-hover:text-[#e866dd] group-hover:scale-110'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} />
                </svg>
                <span className="text-base font-medium tracking-wide">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User section - Elevated and spacious */}
        <div className="absolute bottom-0 left-0 right-0 p-6 bg-black/20 backdrop-blur-sm border-t border-white/5">
          <div className="flex items-center gap-4 mb-4">
            {user?.picture ? (
              <img
                src={user.picture}
                alt=""
                className="w-12 h-12 rounded-full ring-2 ring-white/20 shadow-lg"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#6d7cff] to-[#e866dd] flex items-center justify-center shadow-lg">
                <span className="text-white text-lg font-semibold">{user?.name?.charAt(0) || 'U'}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{user?.name || 'User'}</p>
              <p className="text-gray-400 text-sm truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full px-4 py-3 text-sm font-medium text-gray-400 hover:text-white bg-white/5 hover:bg-white/10 rounded-xl transition-all duration-200 border border-white/5 hover:border-white/10"
          >
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-72">
        {/* Mobile header */}
        <header className="lg:hidden fixed top-0 left-0 right-0 h-20 bg-obdai-primary/95 backdrop-blur-sm z-10 flex items-center px-6 border-b border-white/5">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-3 -ml-3 text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="ml-4">
            <img src="/logo-icon-white.png" alt="OBDAI" className="h-10 w-auto" />
          </div>
        </header>

        {/* Page content */}
        <main className="pt-20 lg:pt-0 min-h-screen">
          <div className="p-8">{children}</div>
        </main>
      </div>

      {/* Persistent chat bubble — renders on all pages */}
      <ChatBubble />
    </div>
  );
}
